import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  Heart,
  Loader2,
  MapPin,
  ShieldAlert,
} from 'lucide-react';
import { useToast } from '@qh/shared';
import {
  ApiError,
  auth,
  candidateApi,
  candidates,
  publicApi,
  type ApplicabilityBlock,
  type ApplyBlockedReason,
  type CandidateMe,
  type JobDetail,
} from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useSavedJobs } from '../context/SavedJobsContext';
import { useAppliedJobs } from '../context/AppliedJobsContext';
import { useProfileGate } from '../components/profile/ProfileCompletionGate';
import { setDocumentMeta, removeDynamicMeta, setJsonLd, removeJsonLd } from '../lib/metaTags';
import { forgetInviteToken, readInviteToken } from '../lib/inviteToken';
import { useDeclarationGate } from '../components/profile/DeclarationGate';
import './job-detail.css';

const PUBLIC_URL = (import.meta.env.VITE_PUBLIC_URL ?? 'http://localhost:3000').replace(/\/+$/, '');

export function JobDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { candidate, setCandidate } = useAuth();
  const { isJobSaved, saveJob, unsaveJob } = useSavedJobs();
  const { markApplied } = useAppliedJobs();
  const { openHard } = useProfileGate();
  const toast = useToast();
  const { requireDeclarations } = useDeclarationGate();
  const [savePending, setSavePending] = useState(false);

  const [job, setJob] = useState<JobDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyState, setApplyState] = useState<'idle' | 'submitting' | 'success' | 'failed'>('idle');
  const [applyError, setApplyError] = useState<string | null>(null);
  /**
   * Set true alongside `applyError` only when the apply failure is the specific
   * "no primary resume" case (backend `VALIDATION_FAILED` with
   * `details: [{ field: 'resume_id', issue: 'no_primary_resume' }]`). Drives a
   * purpose-built message in ApplyBlock with the word "Upload" rendered as a
   * Link to /dashboard/resumes. Scoped tightly so other apply errors render
   * unchanged.
   */
  const [applyErrorMissingResume, setApplyErrorMissingResume] = useState(false);
  /**
   * Flips on successful POST or after a 409 ALREADY_APPLIED comes back from the server.
   * The public job-detail endpoint is cookieless (per Phase 6.1 hotfix), so its
   * `applicability.has_applied` is always false — this local flag is the only client-side
   * source of truth for the post-submission state.
   */
  const [locallyApplied, setLocallyApplied] = useState(false);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);
    setJob(null);
    publicApi
      .getJob(slug)
      .then((j) => {
        if (alive) setJob(j);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('not_found');
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Could not load this job. Try again in a moment.');
        }
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });
    return () => {
      alive = false;
    };
    // Re-fetch only when the slug changes. Applicability for signed-in users is computed
    // client-side (see effectiveApplicability) — the public detail route is cookieless, so
    // its `applicability` block is the unauthenticated shape regardless of session state.
  }, [slug]);

  // Hydrate `locallyApplied` on mount when the job is loaded AND a candidate is signed in.
  // The public job-detail endpoint is called without cookies (see comment in apiClient
  // request()), so its `has_applied` is always false even when the candidate has already
  // applied — the local-state workaround was the only signal until now, and it didn't
  // survive navigation. This authenticated probe fills the gap.
  useEffect(() => {
    if (!job?.id || !candidate?.id) return;
    let cancelled = false;
    candidateApi.applications
      .hasAppliedToJob(job.id)
      .then((hasApplied) => {
        if (cancelled) return;
        if (hasApplied) setLocallyApplied(true);
      })
      .catch(() => {
        // Silent — keep default false. A real ALREADY_APPLIED still surfaces via the
        // apply submission's 409 path.
      });
    return () => {
      cancelled = true;
    };
  }, [job?.id, candidate?.id]);

  // Set per-page <title>, <meta>, OpenGraph, canonical, and JSON-LD JobPosting markup.
  // CLAUDE.md "Google for Jobs Compatibility" — every job detail page emits structured data.
  useEffect(() => {
    if (!job) return;
    const canonical = `${PUBLIC_URL}/job-seekers/${job.slug}`;
    const description = stripHtml(job.description_html).slice(0, 220);
    setDocumentMeta({
      title: `${job.title} — QuantumHire`,
      description,
      canonical,
      openGraph: {
        title: `${job.title} — QuantumHire`,
        description,
        url: canonical,
        type: 'article',
      },
    });
    setJsonLd('jobposting', buildJobPostingJsonLd(job, canonical));
    return () => {
      removeDynamicMeta();
      removeJsonLd('jobposting');
    };
  }, [job]);

  if (isLoading) {
    return (
      <div className="qh-job-detail qh-job-detail--centered min-h-screen pt-32 pb-20 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }
  if (error === 'not_found' || (!job && !isLoading)) {
    return <NotFoundState />;
  }
  if (error || !job) {
    return (
      <div className="qh-job-detail qh-job-detail--centered min-h-screen pt-32 pb-20 px-4">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-2xl p-8 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  async function handleApply() {
    if (!job) return;
    if (!candidate) {
      navigate(`/signin?return=${encodeURIComponent(`/job-seekers/${job.slug}`)}`);
      return;
    }
    // Re-fetch /me before the completion gate. AuthContext's `candidate` is
    // bootstrapped once on mount and only updates on signin / signup / explicit
    // refresh() — so the in-memory `profile_completed` can be stale if the
    // candidate completed their profile in another tab, after a resume change,
    // or via a path that didn't call refresh(). Mirrors the `11b0287` philosophy
    // for `has_applied`: don't trust the bootstrap snapshot for auth-dependent
    // gates the user is acting on RIGHT NOW. Auth-method agnostic — Google and
    // email/password candidates flow through the identical re-check.
    //
    // Failure mode: a transient /me failure falls back to the cached candidate.
    // Worst case is one extra modal display vs. blocking the user on the network.
    //
    // Button disable: applyState='submitting' is set up-front so the Apply
    // button shows the existing "Submitting…" spinner during the re-check, and
    // is reset to 'idle' if we end up opening the modal (so the user sees a
    // normal Apply button when they come back).
    setApplyState('submitting');
    setApplyError(null);
    let fresh: CandidateMe = candidate;
    try {
      const me = await auth.me();
      fresh = me;
      setCandidate(me);
    } catch {
      // Fall through with cached `candidate`. Already assigned to `fresh`.
    }
    // Phase 7-dashboard-c: incomplete profile triggers the hard gate. After save,
    // the gate fires `submitApply` automatically so the candidate doesn't have to
    // click Apply again.
    if (!fresh.profile_completed) {
      setApplyState('idle');
      openHard({
        jobTitle: job.title,
        onAfterComplete: () => {
          void submitApply();
        },
        // Phase 7-dashboard hotfix round 3 — closing the modal without saving cancels
        // the apply. Surface the reason so the dismissal isn't a silent dead-end.
        onCancel: () => {
          toast.info('Complete your profile to apply for jobs.');
        },
      });
      return;
    }
    void submitApply();
  }

  /**
   * `afterDeclaration` marks the retry that follows the declaration popup (step 5e), so a
   * second DECLARATION_REQUIRED shows the error instead of reopening the popup.
   */
  async function submitApply(afterDeclaration = false) {
    if (!job) return;
    setApplyState('submitting');
    setApplyError(null);
    setApplyErrorMissingResume(false);
    try {
      // Recruitment workflow §6.4 — attribute the apply to the candidate's invite when the
      // invite page saved a token for this job; the server ignores a token that isn't theirs.
      const inviteToken = readInviteToken(job.id);
      await candidates.apply({ job_id: job.id, ...(inviteToken ? { invite_token: inviteToken } : {}) });
      forgetInviteToken(job.id);
      setApplyState('success');
      setLocallyApplied(true);
      // Cross-page provider update — listing cards (Saved Jobs, Jobs listing)
      // see this jobId as applied without their own refetch. Additive to the
      // existing locallyApplied flag (which drives THIS page's button only).
      markApplied(job.id);
    } catch (err) {
      // ALREADY_APPLIED is a server-truth signal that the user has already applied — flip
      // the local flag so the UI reflects "Applied" instead of an error toast.
      if (err instanceof ApiError && err.code === 'ALREADY_APPLIED') {
        // Nothing left to attribute — drop any saved invite token for this job.
        forgetInviteToken(job.id);
        setLocallyApplied(true);
        setApplyState('success');
        markApplied(job.id);
        return;
      }
      // Recruitment workflow §6.4 (step 5e) — the fraud declaration is missing: collect it in
      // the popup and retry once. The invite token is still saved, so the retry is attributed.
      if (err instanceof ApiError && err.code === 'DECLARATION_REQUIRED' && !afterDeclaration) {
        setApplyState('idle');
        const jobTitle = job.title;
        requireDeclarations({
          jobTitle,
          onDone: () => {
            void submitApply(true);
          },
          onCancel: () => {
            toast.info('Agree to the fraud declaration to apply.');
          },
        });
        return;
      }
      setApplyState('failed');
      if (err instanceof ApiError) {
        // Detect the no-primary-resume case structurally via the details envelope
        // (backend: applications.service.ts emits {field:'resume_id', issue:'no_primary_resume'}).
        // ApplyBlock renders a purpose-built message with a Link on "Upload"; the
        // err.message is still set so any non-link path stays user-facing.
        const missingResume =
          err.code === 'VALIDATION_FAILED' &&
          !!err.details?.some((d) => d.field === 'resume_id' && d.issue === 'no_primary_resume');
        setApplyErrorMissingResume(missingResume);
        setApplyError(err.message);
      } else {
        setApplyError('Could not submit your application. Try again in a moment.');
      }
    }
  }

  async function handleToggleSave() {
    if (!job) return;
    if (!candidate) {
      navigate(`/signin?return=${encodeURIComponent(`/job-seekers/${job.slug}`)}`);
      return;
    }
    const currentlySaved = isJobSaved(job.id);
    setSavePending(true);
    try {
      if (currentlySaved) {
        await unsaveJob(job.id);
        toast.success('Removed from saved jobs.');
      } else {
        await saveJob(job.id);
        toast.success('Job saved.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update saved status.');
    } finally {
      setSavePending(false);
    }
  }

  // Effective applicability — see comment on `locallyApplied`. The server-returned
  // `job.applicability` is the unauthenticated shape now that the public detail route is
  // cookieless; we recompute it from local state when the visitor is signed in.
  const effectiveApplicability: ApplicabilityBlock = job
    ? computeLocalApplicability(candidate, job, locallyApplied)
    : { has_applied: false, can_apply: false, apply_blocked_reason: 'NOT_AUTHENTICATED' };

  return (
    <div className="qh-job-detail min-h-screen pt-28 pb-20 bg-gray-50">
      <div className="qh-job-detail__wrap max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/job-seekers" className="qh-job-detail__back inline-flex items-center text-sm text-brand-muted font-bold uppercase tracking-widest mb-6 hover:text-brand-primary">
          ← All jobs
        </Link>

        <header className="qh-job-detail__header bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 mb-6">
          <div className="qh-job-detail__identity flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 mb-6">
            <div className="qh-job-detail__heading flex-1 min-w-0">
              <h1 className="qh-job-detail__title text-2xl sm:text-3xl md:text-4xl font-display font-bold text-brand-primary">
                {job.title}
              </h1>
              <div className="qh-job-detail__meta mt-4 flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 text-brand-muted font-medium">
                  <MapPin className="w-3 h-3" />
                  {job.region.name}
                  {job.cities.length > 0 ? ` · ${job.cities.map((c) => c.name).join(', ')}` : ''}
                </span>
                {job.employment_type && <Tag>{job.employment_type.name}</Tag>}
                {job.work_arrangement && <Tag>{job.work_arrangement.name}</Tag>}
                {job.experience_level && <Tag>{job.experience_level.name}</Tag>}
                {job.published_at && (
                  <span className="inline-flex items-center gap-1 text-brand-muted font-medium">
                    <Clock className="w-3 h-3" />
                    Posted {new Date(job.published_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              {job.salary.visible && (
                <p className="qh-job-detail__salary mt-4 text-base sm:text-lg font-bold text-brand-primary">
                  {job.salary.currency?.symbol ?? ''}
                  {job.salary.min.toLocaleString('en-US')} – {job.salary.currency?.symbol ?? ''}
                  {job.salary.max.toLocaleString('en-US')}
                  {job.salary.pay_period ? ` / ${job.salary.pay_period.name.toLowerCase()}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="qh-job-detail__actions flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="qh-job-detail__apply-area flex-1 min-w-0">
              <ApplyBlock
                applicability={effectiveApplicability}
                applyState={applyState}
                applyError={applyError}
                applyErrorMissingResume={applyErrorMissingResume}
                onApply={handleApply}
                candidateRegion={candidate?.region.name ?? null}
                jobRegion={job.region.name}
              />
            </div>
            <SaveJobToggle
              isSaved={candidate ? isJobSaved(job.id) : false}
              loading={savePending}
              onToggle={handleToggleSave}
              isAnonymous={!candidate}
            />
          </div>
        </header>

        <article className="qh-job-detail__panel qh-job-detail__description bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 mb-6">
          <h2 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-3">About the role</h2>
          <div
            className="qh-job-detail__prose prose prose-sm sm:prose-base max-w-none text-brand-primary"
            // The backend sanitizes job description HTML on write (sanitize-html allowlist:
            // h1-h6, p, strong, em, ul/ol/li, a, code, pre, blockquote, br). The string is
            // safe to render — see backend/src/common/sanitize.ts.
            dangerouslySetInnerHTML={{ __html: job.description_html }}
          />
        </article>

        {(job.responsibilities || job.qualifications || job.experience_requirements || job.education_requirements) && (
          <div className="qh-job-detail__facts grid sm:grid-cols-2 gap-4 mb-6">
            {job.responsibilities && (
              <FactBlock title="Responsibilities" body={job.responsibilities} />
            )}
            {job.qualifications && <FactBlock title="Qualifications" body={job.qualifications} />}
            {job.experience_requirements && (
              <FactBlock title="Experience" body={job.experience_requirements} />
            )}
            {job.education_requirements && (
              <FactBlock title="Education" body={job.education_requirements} />
            )}
          </div>
        )}

        {job.skills.length > 0 && (
          <section className="qh-job-detail__panel qh-job-detail__skills bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 mb-6">
            <h2 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-3">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((s) => (
                <span
                  key={s.slug}
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    s.is_required ? 'bg-brand-primary text-white' : 'bg-gray-100 text-brand-primary'
                  }`}
                >
                  {s.name}
                  {s.is_required ? ' · required' : ''}
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ApplyBlock({
  applicability,
  applyState,
  applyError,
  applyErrorMissingResume,
  onApply,
  candidateRegion,
  jobRegion,
}: {
  applicability: JobDetail['applicability'];
  applyState: 'idle' | 'submitting' | 'success' | 'failed';
  applyError: string | null;
  applyErrorMissingResume: boolean;
  onApply: () => void;
  candidateRegion: string | null;
  jobRegion: string;
}) {
  const reason = applicability.apply_blocked_reason;
  const blockedMessage = useMemo(() => blockedReasonMessage(reason, candidateRegion, jobRegion), [reason, candidateRegion, jobRegion]);

  if (applicability.has_applied || applyState === 'success') {
    return (
      <div className="qh-job-detail__state qh-job-detail__state--applied flex items-center gap-3 px-5 py-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800">
        <CheckCircle2 className="w-5 h-5" />
        <p className="font-bold text-sm">Applied — your application is in.</p>
      </div>
    );
  }

  if (applicability.can_apply) {
    return (
      <div className="qh-job-detail__apply-control">
        <button
          onClick={onApply}
          disabled={applyState === 'submitting'}
          className="qh-job-detail__primary-button px-8 py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-brand-deep transition-all disabled:opacity-60 inline-flex items-center gap-2"
        >
          {applyState === 'submitting' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
            </>
          ) : (
            <>Apply now</>
          )}
        </button>
        {applyError && (
          <p className="qh-job-detail__apply-error mt-3 text-sm text-red-700 font-medium">
            {applyErrorMissingResume ? (
              <>
                <Link to="/dashboard/resumes" className="font-bold underline hover:text-red-900">
                  Upload
                </Link>
                {' a resume before applying.'}
              </>
            ) : (
              applyError
            )}
          </p>
        )}
      </div>
    );
  }

  if (reason === 'NOT_AUTHENTICATED') {
    return (
      <div className="qh-job-detail__auth-actions flex flex-col sm:flex-row sm:items-center gap-3">
        <Link
          to={`/signin?return=${encodeURIComponent(window.location.pathname)}`}
          className="qh-job-detail__primary-button px-8 py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-brand-deep transition-all text-center"
        >
          Sign in to apply
        </Link>
        <Link
          to="/signup"
          className="qh-job-detail__secondary-button px-8 py-4 border border-gray-200 text-brand-primary rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-white transition-all text-center"
        >
          Create profile
        </Link>
      </div>
    );
  }

  return (
    <div className="qh-job-detail__state qh-job-detail__state--blocked flex items-start gap-3 px-5 py-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900">
      <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-bold text-sm">{blockedMessage.title}</p>
        {blockedMessage.body && <p className="text-sm mt-1">{blockedMessage.body}</p>}
      </div>
    </div>
  );
}

/**
 * Mirrors the backend's Phase 3 §3.3 applicability guard order, computed from data the
 * SPA already has on hand (AuthContext + job payload). Used when the public job-detail
 * endpoint can't run the server-side check itself because the request is cookieless.
 *
 * `has_applied` is unknown until the user attempts to apply — the server's
 * `ALREADY_APPLIED` 409 surfaces during apply submission and flips the local flag.
 */
function computeLocalApplicability(
  candidate: CandidateMe | null,
  job: JobDetail,
  locallyApplied: boolean,
): ApplicabilityBlock {
  if (!candidate) {
    return { has_applied: false, can_apply: false, apply_blocked_reason: 'NOT_AUTHENTICATED' };
  }
  if (locallyApplied) {
    return { has_applied: true, can_apply: false, apply_blocked_reason: 'ALREADY_APPLIED' };
  }
  if (candidate.status === 'SUSPENDED') {
    return { has_applied: false, can_apply: false, apply_blocked_reason: 'ACCOUNT_SUSPENDED' };
  }
  if (!candidate.email_verified_at) {
    return { has_applied: false, can_apply: false, apply_blocked_reason: 'EMAIL_NOT_VERIFIED' };
  }
  if (candidate.region.slug !== job.region.slug) {
    return { has_applied: false, can_apply: false, apply_blocked_reason: 'REGION_MISMATCH' };
  }
  return { has_applied: false, can_apply: true, apply_blocked_reason: null };
}

function blockedReasonMessage(reason: ApplyBlockedReason, candidateRegion: string | null, jobRegion: string) {
  switch (reason) {
    case 'EMAIL_NOT_VERIFIED':
      return { title: 'Verify your email to apply.', body: 'Check your inbox for the verification link we sent at signup.' };
    case 'ACCOUNT_SUSPENDED':
      return { title: 'Your account is suspended.', body: 'Contact support to reactivate.' };
    case 'REGION_MISMATCH':
      return {
        title: `This job is for ${jobRegion}.`,
        body: candidateRegion ? `Your account is registered for ${candidateRegion}, so it isn't eligible for this role.` : null,
      };
    case 'JOB_NOT_APPLICABLE':
      return { title: 'This job is no longer accepting applications.', body: null };
    default:
      return { title: 'You can\'t apply to this job right now.', body: null };
  }
}

function SaveJobToggle({
  isSaved,
  loading,
  onToggle,
  isAnonymous,
}: {
  isSaved: boolean;
  loading: boolean;
  onToggle: () => void;
  isAnonymous: boolean;
}) {
  const label = isAnonymous ? 'Save job' : isSaved ? 'Saved' : 'Save job';
  const heartFill = isSaved ? 'fill-brand-accent text-brand-accent' : 'text-brand-muted';
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={loading}
      aria-pressed={isSaved}
      aria-label={isSaved ? 'Remove from saved jobs' : 'Save job'}
      className={[
        'qh-job-detail__save-button',
        'inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border font-black text-xs uppercase tracking-widest transition-all disabled:opacity-60 shrink-0',
        isSaved
          ? 'border-brand-accent/40 bg-brand-secondary/40 text-brand-deep hover:bg-brand-secondary'
          : 'border-gray-200 bg-white text-brand-primary hover:bg-gray-50',
      ].join(' ')}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Heart className={`w-4 h-4 ${heartFill}`} />
      )}
      <span>{label}</span>
    </button>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="qh-job-detail__tag px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-gray-100 text-brand-primary">
      {children}
    </span>
  );
}

function FactBlock({ title, body }: { title: string; body: string }) {
  return (
    <section className="qh-job-detail__panel qh-job-detail__fact bg-white border border-gray-200 rounded-3xl p-6">
      <h3 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-2">{title}</h3>
      <p className="text-sm text-brand-primary whitespace-pre-line">{body}</p>
    </section>
  );
}

function NotFoundState() {
  return (
    <div className="qh-job-detail qh-job-detail--centered min-h-screen pt-32 pb-20 bg-gray-50 px-4">
      <div className="qh-job-detail__not-found max-w-xl mx-auto text-center">
        <h1 className="text-4xl font-display font-bold text-brand-primary mb-3">Job not found</h1>
        <p className="text-brand-muted font-medium mb-6">
          This job may have been closed, expired, or removed by the company.
        </p>
        <Link
          to="/job-seekers"
          className="qh-job-detail__primary-button px-6 py-3 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-brand-deep transition-all"
        >
          Browse open jobs
        </Link>
      </div>
    </div>
  );
}

function stripHtml(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
}

interface JsonLdJobPosting {
  '@context': string;
  '@type': string;
  title: string;
  description: string;
  identifier: { '@type': 'PropertyValue'; name: string; value: string };
  datePosted: string | undefined;
  validThrough?: string;
  employmentType?: string;
  hiringOrganization: { '@type': 'Organization'; name: string; sameAs?: string };
  jobLocation: {
    '@type': 'Place';
    address: { '@type': 'PostalAddress'; addressLocality?: string; addressRegion?: string; addressCountry?: string };
  }[];
  baseSalary?: {
    '@type': 'MonetaryAmount';
    currency: string;
    value: { '@type': 'QuantitativeValue'; minValue: number; maxValue: number; unitText: string };
  };
  url: string;
  directApply: true;
}

function buildJobPostingJsonLd(job: JobDetail, canonical: string): JsonLdJobPosting {
  const employmentTypeMap: Record<string, string> = {
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    contract: 'CONTRACTOR',
    contractor: 'CONTRACTOR',
    internship: 'INTERN',
    intern: 'INTERN',
    temporary: 'TEMPORARY',
  };
  const countryMap: Record<string, string> = {
    india: 'IN',
    usa: 'US',
    canada: 'CA',
    thailand: 'TH',
  };
  const employmentTypeKey = job.employment_type?.slug?.toLowerCase();
  const ld: JsonLdJobPosting = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description_html,
    identifier: {
      '@type': 'PropertyValue',
      name: 'QuantumHire',
      value: job.identifier,
    },
    datePosted: job.date_posted ?? job.published_at ?? undefined,
    employmentType: employmentTypeKey ? employmentTypeMap[employmentTypeKey] : undefined,
    hiringOrganization: {
      '@type': 'Organization',
      name: 'QuantumHire',
    },
    jobLocation: (job.cities.length > 0 ? job.cities : [{ slug: '', name: job.region.name }]).map((c) => ({
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: c.name,
        addressRegion: job.region.name,
        addressCountry: countryMap[job.region.slug] ?? undefined,
      },
    })),
    url: canonical,
    directApply: true,
  };
  if (job.valid_through) ld.validThrough = job.valid_through;
  if (job.salary.visible && job.salary.currency && job.salary.pay_period) {
    const payPeriodToUnitText: Record<string, string> = {
      hour: 'HOUR',
      day: 'DAY',
      week: 'WEEK',
      month: 'MONTH',
      year: 'YEAR',
    };
    const unit = payPeriodToUnitText[job.salary.pay_period.slug] ?? 'YEAR';
    ld.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job.salary.currency.code,
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salary.min,
        maxValue: job.salary.max,
        unitText: unit,
      },
    };
  }
  return ld;
}
